'use client';

import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { isCloudflareImageUrl, cfImage, cfImageSrcSet, SIZES } from '@/lib/cloudflare-images';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Copy button component for code blocks
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      try {
        if (typeof document === 'undefined' || !document.body) {
          console.error('Failed to copy: document.body not available');
          return;
        }
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          console.error('Failed to copy');
        }
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-all opacity-100 sm:opacity-90 sm:group-hover:opacity-100 focus:opacity-100"
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Copy
        </span>
      )}
    </button>
  );
}

/**
 * Inline code with click-to-copy functionality
 * Shows visual feedback when clicked
 */
function CopyableInlineCode({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = String(children);

  const handleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers
      try {
        if (typeof document === 'undefined' || !document.body) {
          console.error('Failed to copy: document.body not available');
          return;
        }
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          console.error('Failed to copy');
        }
        document.body.removeChild(textArea);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, [text]);

  return (
    <code
      onClick={handleClick}
      className={`cursor-pointer transition-all duration-200 ${
        copied
          ? 'bg-green-100 text-green-800 ring-2 ring-green-300'
          : 'hover:bg-gray-200 hover:ring-2 hover:ring-gray-300'
      }`}
      title={copied ? 'Copied!' : 'Click to copy'}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {copied ? (
        <span className="inline-flex items-center gap-1">
          <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {children}
        </span>
      ) : (
        children
      )}
    </code>
  );
}

/**
 * Extract text content from React children
 */
function getTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (typeof children === 'object' && 'props' in children) {
    const element = children as React.ReactElement<{ children?: React.ReactNode }>;
    return getTextContent(element.props?.children);
  }
  return '';
}

export default function MarkdownRenderer({ content, className = 'prose prose-lg max-w-none text-gray-700' }: MarkdownRendererProps) {
  return (
    <div className={className} suppressHydrationWarning>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Ensure all images have alt text for SEO and accessibility
          // Use responsive Cloudflare Images for better performance
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          img: ({ src, alt, title, node, ...props }) => {
            // Use alt if provided, otherwise use title, otherwise generate from src
            const srcStr = typeof src === 'string' ? src.trim() : '';
            if (!srcStr) return null;
            const altText = alt || title || `Image: ${srcStr.split('/').pop()?.split('?')[0] || 'content image'}`;

            // For Cloudflare Images, use responsive srcSet
            if (isCloudflareImageUrl(srcStr)) {
              const srcSet = cfImageSrcSet(srcStr, 'content');
              const defaultSrc = cfImage(srcStr, { width: 1024, quality: 85 });

              // eslint-disable-next-line @next/next/no-img-element
              return (
                <img
                  src={defaultSrc}
                  srcSet={srcSet}
                  sizes={SIZES.content}
                  alt={altText}
                  title={title}
                  loading="lazy"
                  {...props}
                />
              );
            }

            // eslint-disable-next-line @next/next/no-img-element
            return <img src={srcStr} alt={altText} title={title} loading="lazy" {...props} />;
          },
          // tabIndex + role + aria-label keep the scrollable region keyboard-accessible
          // (axe scrollable-region-focusable). Prose CSS gives <pre> overflow-x: auto.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          pre: ({ children, node, ...props }) => {
            const codeText = getTextContent(children);
            return (
              <div className="relative group code-block-wrapper">
                <pre {...props} tabIndex={0} role="region" aria-label="Code block" suppressHydrationWarning>{children}</pre>
                <CopyButton text={codeText} />
              </div>
            );
          },
          // Inline code gets click-to-copy functionality
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          code: ({ children, className, node, ...props }) => {
            // Determine if this is block code (inside <pre>) or inline code
            // Block code: has language class OR contains newlines (multi-line code blocks)
            // Inline code: single line, no language class
            const codeText = getTextContent(children);
            const hasLanguageClass = className?.startsWith('language-');
            const isMultiLine = codeText.includes('\n');

            // Block code is inside <pre> - has language class or multiple lines
            const isBlockCode = hasLanguageClass || isMultiLine;

            if (!isBlockCode) {
              // Inline code - apply interactive styles
              return <CopyableInlineCode {...props}>{children}</CopyableInlineCode>;
            }

            // Fenced code blocks (inside <pre>) - render normally without interactive class
            return <code className={className} {...props} suppressHydrationWarning>{children}</code>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
